import { useCallback, useState } from 'react';

/**
 * Persistable column-visibility state.
 *
 * - `defaultHidden` opts a column out by default; the stored value (if any)
 *   wins over both default-visible and default-hidden.
 * - Persists to `localStorage` keyed by `storageKey`. Pass `null` to disable
 *   persistence (e.g. for ephemeral sub-tables).
 *
 * Usage:
 *
 * ```tsx
 * const v = useColumnVisibility('atek_invoices_columns', {
 *   subtotal: { defaultHidden: true },
 *   tax:      { defaultHidden: true },
 * });
 * const visibleColumns = COLUMNS.filter((c) => v.isVisible(c.key));
 * <DropdownMenuCheckboxItem
 *   checked={v.isVisible('subtotal')}
 *   onCheckedChange={() => v.toggle('subtotal')}
 * >Subtotal</DropdownMenuCheckboxItem>
 * ```
 */
export interface ColumnVisibilityOptions {
  defaultHidden?: boolean;
}

export interface UseColumnVisibilityResult {
  /** True iff the column should be rendered. */
  isVisible: (key: string) => boolean;
  /** Flip one column. */
  toggle: (key: string) => void;
  /** Set one column explicitly. */
  set: (key: string, visible: boolean) => void;
  /** Reset all columns to their defaults (clears the stored override). */
  reset: () => void;
  /** Raw visibility map, for advanced consumers. */
  visibility: Record<string, boolean>;
}

function load(storageKey: string | null): Record<string, boolean> {
  if (!storageKey) return {};
  try {
    return JSON.parse(localStorage.getItem(storageKey) || '{}');
  } catch {
    return {};
  }
}

function persist(storageKey: string | null, value: Record<string, boolean>) {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // localStorage unavailable
  }
}

export function useColumnVisibility(
  storageKey: string | null,
  columns: Record<string, ColumnVisibilityOptions> = {},
): UseColumnVisibilityResult {
  const [visibility, setVisibility] = useState<Record<string, boolean>>(() => load(storageKey));

  const isVisible = useCallback(
    (key: string) => {
      if (key in visibility) return visibility[key];
      return !columns[key]?.defaultHidden;
    },
    [visibility, columns],
  );

  const set = useCallback(
    (key: string, visible: boolean) => {
      setVisibility((prev) => {
        const next = { ...prev, [key]: visible };
        persist(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  const toggle = useCallback(
    (key: string) => {
      setVisibility((prev) => {
        const current = key in prev ? prev[key] : !columns[key]?.defaultHidden;
        const next = { ...prev, [key]: !current };
        persist(storageKey, next);
        return next;
      });
    },
    [columns, storageKey],
  );

  const reset = useCallback(() => {
    setVisibility({});
    persist(storageKey, {});
  }, [storageKey]);

  return { isVisible, toggle, set, reset, visibility };
}
